import { useGetProductBrandsPaginated } from "@/hooks/products/use-product-brands";
import {
  type MRT_ColumnDef,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { ProductBrand } from "@/types/products/api-types/product-brand-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { createNameFilter } from "@/lib/table-filters";

interface BrandsTableProps {
  onBrandClick: (brand: ProductBrand) => void;
  onDeleteClick: (brand: ProductBrand) => void;
}

const BrandsTable = ({ onBrandClick, onDeleteClick }: BrandsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<ProductBrand>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const brand = row.original;
          return (
            <button
              onClick={() => onBrandClick?.(brand)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const brand = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(brand)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ],
    [onBrandClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetProductBrandsPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createNameFilter(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="product brands"
        contentTopic="product brand"
      />
    </>
  );
};

export default BrandsTable;
